export const asDateOrNull = (v: unknown) => (v == null || v === "" ? null : (v as Date));
export const isDefined = <T>(value: T | null | undefined): value is T => value != null;
export const ANCH_LOCATIONS = new Set(["FOAA", "DOAA"]);
export const SEA_LOCATION = "SEA";
export const OPERATIONS_LOCATIONS = new Set([
  "FUJT",
  "OPS",
  "FOTT",
  "MAR",
  "VPK",
  "ADNOC",
  "DIBMAR",
  "DIBOPS",
]);

export type MovementFieldKey =
  | "rts"
  | "rta"
  | "rtd"
  | "eta"
  | "etd"
  | "ets"
  | "etc"
  | "ats"
  | "anchorAweighTime"
  | "anchorTime"
  | "pilotUnderway"
  | "pilotAway"
  | "pob"
  | "firstLineAshore"
  | "allFast"
  | "pilotDisembark"
  | "allLinesClear"
  | "allClear"
  | "permissionToSail"
  | "pilotStation";

const FIELD_LANG_BASE = "vessel-call.movement-services.update-form.fields";

export const DEFAULT_FIELD_LABEL_KEYS: Record<MovementFieldKey, string> = {
  rts: `${FIELD_LANG_BASE}.rts`,
  rta: `${FIELD_LANG_BASE}.rta`,
  rtd: `${FIELD_LANG_BASE}.rtd`,
  eta: `${FIELD_LANG_BASE}.eta`,
  etd: `${FIELD_LANG_BASE}.etd`,
  ets: `${FIELD_LANG_BASE}.ets`,
  etc: `${FIELD_LANG_BASE}.etc`,
  ats: `${FIELD_LANG_BASE}.ats`,
  anchorAweighTime: `${FIELD_LANG_BASE}.anchor-aweigh-time`,
  anchorTime: `${FIELD_LANG_BASE}.anchor-time`,
  pilotUnderway: `${FIELD_LANG_BASE}.pilot-underway`,
  pilotAway: `${FIELD_LANG_BASE}.pilot-away`,
  pob: `${FIELD_LANG_BASE}.pob`,
  firstLineAshore: `${FIELD_LANG_BASE}.first-line-ashore`,
  allFast: `${FIELD_LANG_BASE}.all-fast`,
  pilotDisembark: `${FIELD_LANG_BASE}.pilot-disembark`,
  allLinesClear: `${FIELD_LANG_BASE}.all-lines-clear`,
  allClear: `${FIELD_LANG_BASE}.all-clear`,
  permissionToSail: `${FIELD_LANG_BASE}.permission-to-sail`,
  pilotStation: `${FIELD_LANG_BASE}.pilot-station`,
};

export type UpdateMovementConfig = {
  visibleFields: MovementFieldKey[];
  labelKeys?: Partial<Record<MovementFieldKey, string>>;
};

// Configuration for Movement Times and Estimates after approval
// Provide labelKeys only for fields whose default translation key should be overridden.
export const UPDATE_MOVEMENT_CONFIG: Partial<Record<string, UpdateMovementConfig>> = {
  SEA_TO_BERTH: {
    visibleFields: [
      "eta",
      "ets",
      "pilotStation",
      "pilotUnderway",
      "pob",
      "firstLineAshore",
      "allFast",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      eta: `${FIELD_LANG_BASE}.eta-readiness`,
      ets: `${FIELD_LANG_BASE}.ets-readiness`,
      ats: `${FIELD_LANG_BASE}.actual-time-start`,
      pob: `${FIELD_LANG_BASE}.pilot-on-board`,
      pilotDisembark: `${FIELD_LANG_BASE}.pilot-disembarked`,
      pilotStation: `${FIELD_LANG_BASE}.pilot-boarding-area`,
    },
  },
  BERTH_TO_SEA: {
    visibleFields: ["etd", "ets", "pilotUnderway", "pob", "allClear", "pilotDisembark", "ats"],
    labelKeys: {
      etd: `${FIELD_LANG_BASE}.etd-readiness`,
      ets: `${FIELD_LANG_BASE}.ets-readiness`,
      pob: `${FIELD_LANG_BASE}.pilot-on-board`,
      pilotDisembark: `${FIELD_LANG_BASE}.pilot-disembarked`,
      ats: `${FIELD_LANG_BASE}.actual-time-start`,
    },
  },
  ANCHORAGE_TO_SEA: {
    visibleFields: ["ats", "etd"],
    labelKeys: {
      etd: `${FIELD_LANG_BASE}.etd-readiness`,
      ats: `${FIELD_LANG_BASE}.ats-permission-to-sail`,
    },
  },
  ANCHORAGE_TO_BERTH: {
    visibleFields: [
      "eta",
      "ets",
      "anchorAweighTime",
      "pilotStation",
      "pilotUnderway",
      "pob",
      "firstLineAshore",
      "allFast",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      eta: `${FIELD_LANG_BASE}.eta-readiness`,
      ets: `${FIELD_LANG_BASE}.ets-readiness`,
      anchorAweighTime: `${FIELD_LANG_BASE}.anchor-aweigh-time`,
      pilotStation: `${FIELD_LANG_BASE}.pilot-boarding-area`,
      pob: `${FIELD_LANG_BASE}.pilot-on-board`,
      pilotDisembark: `${FIELD_LANG_BASE}.pilot-disembarked`,
      ats: `${FIELD_LANG_BASE}.actual-time-start`,
    },
  },
  SEA_TO_ANCHORAGE: {
    visibleFields: ["eta", "ats"],
    labelKeys: {
      eta: `${FIELD_LANG_BASE}.eta-readiness`,
      ats: `${FIELD_LANG_BASE}.ats-permission-to-anchor`,
    },
  },
  BERTH_TO_BERTH: {
    visibleFields: [
      "etd",
      "eta",
      "ets",
      "pilotUnderway",
      "pob",
      "allClear",
      "firstLineAshore",
      "allFast",
      "pilotDisembark",
      "ats",
    ],
    labelKeys: {
      etd: `${FIELD_LANG_BASE}.etd-readiness`,
      eta: `${FIELD_LANG_BASE}.eta-readiness`,
      ets: `${FIELD_LANG_BASE}.ets-readiness`,
      pob: `${FIELD_LANG_BASE}.pilot-on-board`,
      pilotDisembark: `${FIELD_LANG_BASE}.pilot-disembarked`,
      ats: `${FIELD_LANG_BASE}.actual-time-start`,
    },
  },
};

export const DEFAULT_FIELDS: MovementFieldKey[] = [
  "rts",
  "rta",
  "rtd",
  "ets",
  "etc",
  "ats",
  "pob",
  "allLinesClear",
  "pilotStation",
];

export const ANCH_FIELDS: MovementFieldKey[] = [
  "rts",
  "rta",
  "rtd",
  "ets",
  "etc",
  "ats",
  "pilotUnderway",
  "pob",
  "allClear",
  "pilotAway",
  "anchorTime",
  "anchorAweighTime",
  "pilotStation",
];
export const OPERATIONS_FIELDS: MovementFieldKey[] = [
  "rts",
  "rta",
  "rtd",
  "ets",
  "etc",
  "ats",
  "anchorAweighTime",
  "pob",
  "pilotUnderway",
  "firstLineAshore",
  "allFast",
  "pilotDisembark",
  "allClear",
  "pilotStation",
];
export const SEA_FIELDS: MovementFieldKey[] = [
  "rts",
  "rta",
  "rtd",
  "eta",
  "etd",
  "ets",
  "etc",
  "ats",
  "permissionToSail",
  "pob",
  "allLinesClear",
  "anchorAweighTime",
  "pilotStation",
];
